import { InjectionToken, InputSignal, Signal } from '@angular/core';
import { IqCssClassnames, IqCssClassNamesRule } from '@dat/iq-style-processor';
import { EventInfo, EventQuery } from '@dat/iq-event-bus';
import { z } from 'zod';

/**
 * The value to compare to the event data to. Depending on the value, only
 * certain operators can be used. For example, if the value is a string, you can use
 * eq, ne, startsWith.
 */
//   value: boolean | string | number | any[];

/**
 * An operator for the query rules. These are the ways we can compare
 * the value in the rule to the current data within the event. We support
 * the following operators:
 *
 * - eq: Equals - This is the strict equal. (Can be used for anything)
 * - ne: Not Equals - This is the strict not equal. (Can be used for anything)
 * - gt: Greater Than - Checks that the value in the event is greater than the value that is in the query. (Can be used for numbers)
 * - lt: Less Than - Checks that the value in the event is less than the value that is in the query. (Can be used for numbers)
 * - gte: Greater Than or Equal - Checks that the value in the event is greater than or equal to the value that is in the query. (Can be used for numbers)
 * - lte: Less Than or Equal - Checks that the value in the event is less than or equal to the value that is in the query. (Can be used for numbers)
 * - startsWith: Starts With - Checks that the value in the event starts with the substring. (Can be used for strings)
 * - in: In - Checks that the value in the event is in the array of values. (Can be used for arrays)
 * - nin: Not In - Checks that the value in the event is not in the array of values. (Can be used for arrays)
 */
//   operator: EventQueryOperator;

const EventQueryOperator = z
    .union([
        z.literal('eq'),
        z.literal('ne'),
        z.literal('gt'),
        z.literal('lt'),
        z.literal('gte'),
        z.literal('lte'),
        z.literal('startsWith'),
        z.literal('in'),
        z.literal('nin'),
    ])
    .default('eq');

const EventQueryValue = z
    .union([z.boolean(), z.string(), z.number(), z.array(z.any())])
    .default('');

const EventQueryRuleSchema = z.object({
    value: EventQueryValue,
    operator: EventQueryOperator,
});

/**
 * This event object is used to indicate the source event which is
 * the event that triggers the event info.
 *
 * It takes in an source event rule so we can make sure the target event
 * only fires if the rule matches the information in the source event info.
 *
 * Also it has a ruleOperator and rules based on the RuleEvaluationSchema
 * which based on evaluation will determine if the event should fire.
 *
 */
export interface TriggerEvent extends EventInfo {
    /**
     * This is the source event rule. These are different from the rule array since
     * these used more to match the specific source action to trigger it.
     *
     * This is to prevent us from having the same action fire on the same dashboard, component, section
     * and triggering an event it didn't mean to.
     *
     * For example, if we have two forms on the same dashboard. We only want the segment event for Form 1 to fire
     * when Form 1 is submitted. And we only want Form 2 to fire its submit action when Form 2 is submitted.
     */
    sourceEventRule?: EventQuery;
    sourceEventRuleOperator?: 'AND' | 'OR' | 'NOT';

    ruleOperator?: 'AND' | 'OR' | 'NOT';

    /**
     * This is a rule to check for things outside the source event just in case want to stop it from firing
     * due to feature flags or environment variables.
     */
    rules?: { [key: string]: any; type: string }[];
}

const EventQueryRuleDataSchema = z
    .object({
        path: z.string(),
    })
    .merge(EventQueryRuleSchema);

export const SharedSchema = z.object({
    id: z.string(),
    cache: z.record(z.object({})),
    loaded: z.boolean().optional(),
    cacheable: z.boolean().optional(),
    persistKeys: z.array(z.string()).optional(),
    loadStateCache: z.boolean().optional(),
    eventCacheRules: z
        .array(
            z.object({
                name: EventQueryRuleSchema.optional(),
                payload: z.array(EventQueryRuleDataSchema).optional(),
                fired: EventQueryRuleSchema.optional(),
                handled: EventQueryRuleSchema.optional(),
            }),
        )
        .optional(),
});

type SharedActions = Partial<{
    ruleOperator?: 'AND' | 'OR' | 'NOT';
    rules: { [key: string]: string }[];
    /**
     * @deprecated - Use cssClassRules instead
     */
    classNames: IqCssClassnames;
    cssClassRules: IqCssClassNamesRule[];
    cssClassRuleOperator?: 'AND' | 'OR' | 'NOT';
}>;

type Shared = z.infer<typeof SharedSchema> & SharedActions & TriggerEventType;

export type TriggerEventType = {
    [K in `on${string}`]: TriggerEvent[];
};

export const ComponentTypes = z.union([
    z.literal('Controls'),
    z.literal('RecentSearchesLanes'),
    z.literal('CardRateHistorical'),
    z.literal('CardRateForecast'),
    z.literal('CardRateDynamic'),
    z.literal('ErrorScreen'),
    z.literal('ErrorScreenLarge'),
    z.literal('GraphRate13MForecast'),
    z.literal('GraphRate35DForecast'),
    z.literal('GraphRate13MHistorical'),
    z.literal('GraphRateHybrid'),
    z.literal('TableRate13MHistorical'),
    z.literal('TableRate13MForecast'),
    z.literal('TableRate35DForecast'),
    z.literal('TableContributions'),
    z.literal('Label'),
    z.literal('MCSingleLaneMap'),
    z.literal('Unknown'),
    z.literal('McGaugeQueryLt'),
    z.literal('McGaugeQueryMci'),
    z.literal('MCLoads'),
    z.literal('MCTrucks'),
    z.literal('NtlGraphRateMulti'),
    z.literal('StateRegionMatrix'),
    z.literal('QuoteCalculator'),
    z.literal('RavenEstimate'),
    z.literal('TrendLineSnapshotTable'),
    z.literal('TrendlinesNtlSpotFlatbed'),
    z.literal('TrendlinesNtlSpotReefer'),
    z.literal('TrendlinesNtlSpotVan'),
    z.literal('TrendlineWeeklyBrief'),
    z.literal('TrendlineFuelPrices'),
    z.literal('CardRateNational'),
    z.literal('GraphRateNational3M'),
    z.literal('GraphRateNational6M'),
    z.literal('GraphRateNational1Y'),
    z.literal('McMapMci'),
    z.literal('McMapMciDashboard'),
    z.literal('McMapLt'),
    z.literal('RateLaneTable'),
    z.literal('SocialVideo'),
    z.literal('SocialBlog'),
    z.literal('SocialPodcast'),
    z.literal('SocialHelpCenter'),
    z.literal('McMapLegend'),
    z.literal('TrendlinesLtMapReefer'),
    z.literal('TrendlinesLtMapFlatbed'),
    z.literal('TrendlinesLtMapVan'),
    z.literal('TrendlinesLtGraphReefer'),
    z.literal('TrendlinesLtGraphFlatbed'),
    z.literal('TrendlinesLtGraphVan'),
    z.literal('TrendlinesLtNationalGraphReefer'),
    z.literal('TrendlinesLtNationalGraphFlatbed'),
    z.literal('TrendlinesLtNationalGraphVan'),
    z.literal('TrendlinesRatesMapReefer'),
    z.literal('TrendlinesRatesMapFlatbed'),
    z.literal('TrendlinesRatesMapVan'),
    z.literal('TrendlinesRatesGraphReefer'),
    z.literal('TrendlinesRatesGraphFlatbed'),
    z.literal('TrendlinesRatesGraphVan'),
    z.literal('TrendlinesRatesNationalGraphReefer'),
    z.literal('TrendlinesRatesNationalGraphFlatbed'),
    z.literal('TrendlinesContentViewTrends'),
    z.literal('TrendlinesRatesNationalGraphVan'),
    z.literal('TableContributions'),
    z.literal('RateLaneODSearchFormatter'),
    z.literal('RateLaneFuelLoader'),
    z.literal('RateLaneMileageLoader'),
    z.literal('RateLaneRateLoader'),
    z.literal('IqQuoteCalculator'),
    z.literal('RepoLibraryStatusCard'),
    z.literal('RepoLibraryStatusCardGrid'),
    z.literal('RepoLibraryStatusViz'),
    z.literal('RepoLibraryStatusTable'),
    z.literal('RateLaneEventBusRegister'),
    z.literal('McEventBusRegister'),
    z.literal('GraphRatesAll'),
    z.literal('GraphRatesTime'),
    z.literal('GraphRatesCurrent'),
    z.literal('EventBusAuditTable'),
    z.literal('McGaugeMci'),
    z.literal('McGaugeSection'),
    z.literal('McLtChart'),
    z.literal('FuelSurchargePanel'),
    z.literal('McMciChart'),
    z.literal('RatesTable'),
    z.literal('McModalMci'),
    z.literal('McMapLt'),
    z.literal('McCapacityLabel'),
    z.literal('TrendlinesUpsellBanner'),
    z.literal('TrendLinesFfRegister')
]);

export type ComponentTypeValues = z.infer<typeof ComponentTypes>;

export const ComponentSchema = SharedSchema.merge(
    z.object({
        type: ComponentTypes,
        props: z.record(z.any()),
        propMap: z.record(z.string(), z.string()).optional(),
    }),
);

export const ComponentArraySchema = z.array(ComponentSchema);
//#region Section Schemas
export const SectionTypes = z.union([
    z.literal('Draggable'),
    z.literal('Modal'),
    z.literal('Collapsible'),
]);

export const SharedSectionSchema = SharedSchema.merge(
    z.object({
        title: z.string().optional(),
        titleType: z.union([z.literal('html'), z.literal('md')]).default('md'),
        icon: z.string().optional(),
        groupId: z.string().optional(),
        tooltipBody: z.string().optional(),
        tooltipLabel: z.string().optional(),
        tooltipHeader: z.string().optional(),
        type: SectionTypes.optional(),
    }),
);

export const CollapsibleSectionSchema = SharedSectionSchema.merge(
    z.object({
        type: z.literal('Collapsible'),
        collapsed: z.boolean().default(true),
    }),
);

export const ModalSectionSchema = SharedSectionSchema.merge(
    z.object({
        type: z.literal('Modal'),
    }),
);

export const DraggableSectionSchema = SharedSectionSchema.merge(
    z.object({
        type: z.literal('Draggable'),
    }),
);

export const SectionSchema = z.discriminatedUnion('type', [
    CollapsibleSectionSchema,
    ModalSectionSchema,
    DraggableSectionSchema,
]);

export const SectionArraySectionSchema = z.array(SharedSectionSchema);

export const SectionGroupSchema = z.object({
    id: z.string(),
});

export type SectionGroup = z.infer<typeof SectionGroupSchema>;
//#endregion
export const DashboardSchema = SharedSchema.merge(
    z.object({
        sections: SectionArraySectionSchema,
        sectionGroups: z.array(SectionGroupSchema).optional(),
    }),
);
export type Component = z.infer<typeof ComponentSchema> & Shared;
export type Section = z.infer<typeof SectionSchema> & {
    components: Component[];
} & Shared;

export type Dashboard = z.infer<typeof DashboardSchema> & {
    sections: Section[];
} & Shared;

const shared: Shared = {
    id: '',
    cache: {},
};

export const emptySection: Section = {
    ...shared,
    components: [],
    title: '--',
};
export const emptyDashboard: Dashboard = {
    ...shared,
    sections: [],
};
export const emptyComponent: Component = {
    ...shared,
    type: 'Controls',
    rules: [],
};

export interface IIqBuilderStateService {
    dashboardState(dashboard: Dashboard): Signal<Dashboard>;
    dashboardCache(dashboardId: string): Signal<any>;
    sectionCache(dashboardId: string, sectionId: string): Signal<any>;
}

/**
 * This is the information for the route This only will be
 * added to the event if the component is in a stateful context.
 */
export type RouteInfo = {
    /**
     * The page name for the analytics
     */
    analyticsPageName: string;
    /**
     * Any additional data added to the route derived from when the route
     * was registered
     */
    data: any;

    /**
     * Determines if the route can be navigated
     */
    locked: boolean;

    /**
     * The title that appears in the browser
     */
    pageTitle: string;

    /**
     * The Angular Router Parms. This is a key value pair based
     * on how the route was set up.
     */
    params: any;

    /**
     * The full path without the host
     */
    path: string;

    /**
     * Determines if the route is loaded
     */
    loaded: boolean;
};

export const BUILDER_AUDIT_TOOLS = new InjectionToken<boolean>(
    'Builder Audit Tools',
    {
        providedIn: 'root',
        factory: () => false,
    },
);
